/**
 * Cross-cutting guard helpers shared by every validator.
 *
 * Per the Phase 2B spec:
 *   1. stateVersion guard → ERR_STALE_VERSION
 *   2. Phase guard → ERR_WRONG_PHASE (spec calls this ERR_PHASE_MISMATCH in §14)
 *
 * Rules:
 * - No I/O, no Math.random().
 * - No mutations.
 * - Pure: reads state + version number only.
 *
 * Note: The rules-spec §14 uses `ERR_PHASE_MISMATCH` as the canonical code.
 * The variable name here is `ERR_WRONG_PHASE` for readability in this file,
 * but we emit "ERR_PHASE_MISMATCH" as the stable error code.
 */

import type { ValidationResult } from "../types/common";
import type { GameState, TurnPhase } from "../types/game-state";

/**
 * Checks that `actionVersion` matches `state.version`.
 * Returns `null` (pass) or a failed `ValidationResult`.
 */
export const checkVersion = (
  state: GameState,
  actionVersion: number,
): ValidationResult | null => {
  if (actionVersion !== state.version) {
    return {
      valid: false,
      error: "ERR_STALE_VERSION",
      details: {
        expected: state.version,
        received: actionVersion,
      },
    };
  }
  return null;
};

/**
 * Checks that the turn is in one of the expected `phases`.
 * Returns `null` (pass) or a failed `ValidationResult`.
 */
export const checkPhase = (
  state: GameState,
  ...phases: readonly TurnPhase[]
): ValidationResult | null => {
  if (!phases.includes(state.turn.phase)) {
    return {
      valid: false,
      error: "ERR_PHASE_MISMATCH",
      details: {
        expected: phases,
        actual: state.turn.phase,
      },
    };
  }
  return null;
};

/**
 * Runs version + phase checks in order.
 * Returns the first failure found, or `null` if both pass.
 */
export const commonGuards = (
  state: GameState,
  actionVersion: number,
  ...phases: readonly TurnPhase[]
): ValidationResult | null =>
  checkVersion(state, actionVersion) ?? checkPhase(state, ...phases);

/** Shorthand for a valid result. */
export const VALID: ValidationResult = { valid: true };
